import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaMapPin } from "react-icons/fa";
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../constants/config';
import axiosPost from '../helpers/axios-helper';

import '../styles/Country.css';
import { MdChevronRight } from 'react-icons/md';

export default function Country() {
    const location = useLocation();
    const { slug } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [countryInfo, setCountryInfo] = useState(null);
    const [countryArticles, setCountryArticles] = useState([]);
    const [queerLandscape, setQueerLandscape] = useState(null);
    const [travelGuide, setTravelGuide] = useState(null);
    
    // Resolved country ID (from id query or slug)
    const [countryId, setCountryId] = useState(null);
    
    const [ country, setCountry ] = useState({
        name: 'Curacao',
        image: 'https://e22hgs9rtwc.exactdn.com/wp-content/uploads/2021/11/shutterstock_2262892975.jpg?strip=all&lossy=1&ssl=1',
        intro: {
            title: 'Curacao Travel Guide',
            body: "Discover the vibrant beauty of Curaçao, an island where the shimmering turquoise waters of the Caribbean Sea gently kiss the shores of pristine, white-sand beaches. Picture yourself wandering through the charming streets of Willemstad, the island's capital, where rows of colorful colonial buildings, painted in shades of pastel pinks, yellows, and blues, create a stunning contrast against the bright blue sky. Every corner of this UNESCO World Heritage city tells a story, from its rich history to its vibrant cultural scene."
        }
    })

    const toAbsUrl = (url, fallback = '') => {
        if (!url) return fallback;
        if (/^https?:\/\//i.test(url)) return url;
        return `${config.api.url}${url}`;
    };

    // Strip HTML tags for plain text display in cards
    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').trim();
    };

    // Slugify helper to match country names to slug param
    const slugify = (str) => {
        try {
            return (str || '')
              .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
              .replace(/&/g, ' and ')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');
        } catch { return ''; }
    };

    // Resolve countryId from either id query param or slug route param
    useEffect(() => {
        const qp = new URLSearchParams(location.search);
        const idParam = qp.get('id');
        if (idParam) {
            setCountryId(parseInt(idParam, 10));
            return;
        }
        if (slug) {
            setLoading(true);
            // Fetch list and find by slug of name
            axiosPost(
                'countries/list',
                { 'Content-Type': 'application/json' },
                {},
                (resp) => {
                    const arr = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
                    const found = arr.find(c => slugify(c.name) === slug);
                    if (found) {
                        setCountryId(parseInt(found.id, 10));
                        setError(null);
                    } else {
                        setError('Country not found');
                        setLoading(false);
                    }
                }
            );
        }
    }, [location.search, slug]);

    // Fetch country data once we have a resolved countryId
    useEffect(() => {
        const fetchCountryData = async () => {
            if (!countryId) return; // wait until resolved
            try {
                console.log('Fetching country data for ID:', countryId);
                const response = await axios.post(`${config.api.url}countries/get`, 
                    { id: Number(countryId) },
                    { 
                        headers: {
                            'Content-Type': 'application/json',
                            'api': config.api.key
                        }
                    }
                );
                
                // Store the complete response in countryInfo state
                setCountryInfo(response.data.data[0]);
                
                console.log('API Response:', response.data);
                
                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    // The API returns data in an array, get the first item
                    const countryData = response.data.data[0];
                    console.log('Country data:', countryData);
                    
                    // Update country state with fetched data
                    setCountry(prevState => {
                        const updatedState = {
                            ...prevState,
                            name: countryData.name,
                            image: toAbsUrl(countryData.image, prevState.image),
                            intro: {
                                title: `${countryData.name} Travel Guide`,
                                body: countryData.summary || countryData.info || prevState.intro.body
                            }
                        };
                        return updatedState;
                    });

                    // Fetch related articles for this country (limit 6)
                    try {
                        const artRes = await axios.post(
                            `${config.api.url}articles/by-country`,
                            { country_id: Number(countryId), status: 'published', per_page: 6 },
                            {
                                headers: {
                                    'Content-Type': 'application/json',
                                    'api': config.api.key
                                }
                            }
                        );
                        const items = artRes?.data?.data?.items || [];
                        const mapped = items.map((it) => ({
                            id: it.id,
                            title: it.title,
                            excerpt: it.excerpt,
                            image: toAbsUrl(it.featured_image_url),
                            slug: it.slug,
                            category: it.category || '',
                            author: it.author || 'OutCaribbean'
                        }));
                        setCountryArticles(mapped);
                    } catch (e) {
                        console.warn('Failed to load articles by country', e);
                    }

                    // Fetch hotels for this country (active only) using axiosPost helper
                    axiosPost(
                        'hotels/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const hitems = data?.data?.items || [];
                            const mappedHotels = hitems.map((h) => ({
                                id: h.id,
                                title: h.title,
                                location: h.location || '',
                                description: h.description || '',
                                button: h.button || 'Book Now',
                                image: toAbsUrl(h.image),
                                address: h.address || '',
                            }));
                            setHootels(mappedHotels);
                        }
                    );

                    console.log('Hotels:', hotels);

                    // Fetch restaurants for this country (active only) using axiosPost helper
                    axiosPost(
                        'restaurants/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const ritems = data?.data?.items || [];
                            const mappedRestaurants = ritems.map((r) => {
                                const parseImages = (val) => {
                                    if (!val) return [];
                                    if (Array.isArray(val)) return val.map(v => (v ?? '').toString().trim()).filter(Boolean);
                                    const str = typeof val === 'string' ? val : String(val);
                                    return str
                                        .split(/\r?\n|,/)
                                        .map(s => s.trim())
                                        .filter(Boolean);
                                };
                                const imgs = parseImages(r.images || '');
                                const cover = imgs.length ? imgs[0] : (r.image || '');
                                return {
                                    id: r.id,
                                    title: r.title,
                                    location: r.location || '',
                                    description: r.description || '',
                                    button: r.button || 'Reserve',
                                    image: toAbsUrl(cover)
                                };
                            });
                            setRestaurants(mappedRestaurants);
                        }
                    );

                    // Fetch experiences for this country (active only) using axiosPost helper
                    axiosPost(
                        'experiences/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const eitems = data?.data?.items || [];
                            const mappedExperiences = eitems.map((e) => {
                                const parseImages = (val) => {
                                    if (!val) return [];
                                    if (Array.isArray(val)) return val.map(v => (v ?? '').toString().trim()).filter(Boolean);
                                    const str = typeof val === 'string' ? val : String(val);
                                    return str
                                        .split(/\r?\n|,/)
                                        .map(s => s.trim())
                                        .filter(Boolean);
                                };
                                const imgs = parseImages(e.images || '');
                                const cover = imgs.length ? imgs[0] : (e.image || '');
                                return {
                                    id: e.id,
                                    title: e.title,
                                    location: e.location || '',
                                    description: e.description || '',
                                    button: e.button || 'Learn More',
                                    image: toAbsUrl(cover)
                                };
                            });
                            setExperiencesCarousel(mappedExperiences);
                        }
                    );

                    // Fetch beaches for this country
                    axiosPost(
                        'beaches/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const bitems = data?.data?.items || [];
                            setBeaches(bitems.map((b) => ({
                                id: b.id, title: b.title, location: b.location || '',
                                description: b.description || '', button: 'Explore',
                                image: toAbsUrl(b.images ? String(b.images).split(/\r?\n|,/)[0]?.trim() : b.image)
                            })));
                        }
                    );

                    // Fetch explore items for this country
                    axiosPost(
                        'explore/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const eitems = data?.data?.items || [];
                            setExploreItems(eitems.map((e) => ({
                                id: e.id, title: e.title, location: e.location || '',
                                description: e.description || '', button: 'Discover',
                                image: toAbsUrl(e.images ? String(e.images).split(/\r?\n|,/)[0]?.trim() : e.image)
                            })));
                        }
                    );

                    // Fetch wellness items for this country
                    axiosPost(
                        'wellness/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const witems = data?.data?.items || [];
                            setWellnessItems(witems.map((w) => ({
                                id: w.id, title: w.title, location: w.location || '',
                                description: w.description || '', button: 'Book Now',
                                image: toAbsUrl(w.images ? String(w.images).split(/\r?\n|,/)[0]?.trim() : w.image)
                            })));
                        }
                    );

                    // Fetch play items for this country
                    axiosPost(
                        'play/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const pitems = data?.data?.items || [];
                            setPlayItems(pitems.map((p) => ({
                                id: p.id, title: p.title, location: p.location || '',
                                description: p.description || '', button: 'Learn More',
                                image: toAbsUrl(p.images ? String(p.images).split(/\r?\n|,/)[0]?.trim() : p.image)
                            })));
                        }
                    );

                    // Fetch shop local items for this country
                    axiosPost(
                        'shop-local/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const sitems = data?.data?.items || [];
                            setShopLocalItems(sitems.map((s) => ({
                                id: s.id, title: s.title, location: s.location || '',
                                description: s.description || '', button: 'Visit',
                                image: toAbsUrl(s.images ? String(s.images).split(/\r?\n|,/)[0]?.trim() : s.image)
                            })));
                        }
                    );

                    // Fetch Queer Landscape content for this country
                    axiosPost(
                        'queer-landscape/get',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId) },
                        (data) => {
                            console.log('Queer Landscape API response:', data);
                            if (data?.success && data?.data) {
                                setQueerLandscape(data.data);
                            }
                        },
                        (err) => {
                            console.warn('No Queer Landscape content found for this country', err);
                        }
                    );

                    // Fetch Travel Guide content for this country
                    axiosPost(
                        'travel-guides/get',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId) },
                        (data) => {
                            if (data?.success && data?.data) {
                                setTravelGuide(data.data);
                            }
                        },
                        (err) => {
                            console.warn('No Travel Guide content found for this country', err);
                        }
                    );
                } else {
                    console.error('API request successful but no country data found');
                    throw new Error(response.data.response || 'Failed to fetch country data');
                }
            } catch (err) {
                console.error('Error fetching country data:', err);
                setError(err.response?.data?.message || err.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchCountryData();
    }, [countryId]);

    // Redirect legacy /country?id=... to /country/:slug for canonical URL once name is known
    useEffect(() => {
        if (!countryInfo) return;
        const qp = new URLSearchParams(location.search);
        const hasId = qp.get('id');
        if (hasId && !slug) {
            const s = slugify(countryInfo.name || '');
            if (s) navigate(`/country/${s}`, { replace: true });
        }
    }, [countryInfo, location.search, slug, navigate]);
    
    const [ hotelIndex, setHotelIndex ] = useState(0);
    const [ hotels, setHootels ] = useState([])

    // Experiences carousel (things to do)
    const [ experienceIndex, setExperienceIndex ] = useState(0);
    const [ experiencesCarousel, setExperiencesCarousel ] = useState([
        // {
        //     title: "Snorkel at Playa Lagun",
        //     location: "Curaçao, Lagun",
        //     description: "Swim with sea turtles and explore vibrant coral reefs in one of Curaçao’s most picturesque coves.",
        //     button: "Learn More",
        //     image: "https://www.janthielresort.com/wp-content/uploads/sites/31/2020/03/Curacao-Snorkling-With-Turtles.jpg"
        // },
        // {
        //     title: "Willemstad Street Art Tour",
        //     location: "Curaçao, Willemstad",
        //     description: "Discover colorful murals and historic alleys across Otrobanda and Punda with local artist stories.",
        //     button: "Learn More",
        //     image: "https://www.besabine.com/wp-content/uploads/2023/07/Straatkunst-Willemstad.jpg"
        // },
        // {
        //     title: "Klein Curaçao Day Trip",
        //     location: "Curaçao, Klein Curaçao",
        //     description: "Sail to the uninhabited island for pristine beaches, crystal waters, and a lighthouse photo op.",
        //     button: "Book Trip",
        //     image: "https://klein-curacao.nl/wp-content/uploads/2024/05/miss-ann-boat-trips.jpg"
        // },
        // {
        //     title: "Christoffel Park Hike",
        //     location: "Curaçao, Westpunt",
        //     description: "Hike to the island’s highest peak for sweeping views and native flora and fauna.",
        //     button: "Plan Hike",
        //     image: "https://images.squarespace-cdn.com/content/v1/53b6ec86e4b09a1fb529c6b8/1568171979138-P7QL73DCJMOFE3ZB5B1J/image-asset.jpeg"
        // },
        // {
        //     title: "Shete Boka National Park",
        //     location: "Curaçao, West Coast",
        //     description: "Witness dramatic blowholes and crashing waves along rugged volcanic coastline.",
        //     button: "Explore",
        //     image: "https://www.besabine.com/wp-content/uploads/2023/07/Shete-Boka-Nationaal-Park-Curacao.jpg"
        // },
        // {
        //     title: "Floating Market & Handelskade",
        //     location: "Curaçao, Punda",
        //     description: "Stroll along the iconic waterfront and sample fresh produce from Venezuelan boats.",
        //     button: "Visit",
        //     image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/12/31/80/41/floating-market.jpg?w=1200&h=-1&s=1"
        // }
    ])

    const [ restaurantIndex, setRestaurantIndex ] = useState(0);
    const [ restaurants, setRestaurants ] = useState([])

    // New category carousels
    const [ beachIndex, setBeachIndex ] = useState(0);
    const [ beaches, setBeaches ] = useState([]);
    const [ exploreIndex, setExploreIndex ] = useState(0);
    const [ exploreItems, setExploreItems ] = useState([]);
    const [ wellnessIndex, setWellnessIndex ] = useState(0);
    const [ wellnessItems, setWellnessItems ] = useState([]);
    const [ playIndex, setPlayIndex ] = useState(0);
    const [ playItems, setPlayItems ] = useState([]);
    const [ shopLocalIndex, setShopLocalIndex ] = useState(0);
    const [ shopLocalItems, setShopLocalItems ] = useState([]);

    const [ activeItem, setActiveItem ] = useState(0);
    const [ experiences, setExperiences ] = useState([
        {
            id: 1,
            title: "CURAÇAO MUSEUM",
            body: "Located in Otrobanda's western district, this colonial-style museum showcases Curaçao's rich heritage through geological exhibits, pre-Columbian Indian artifacts, and Caribbean historical collections. Features authentic 18th and 19th century mahogany furniture, historical maps, and artworks from local and international artists. Special focus on afro-Curaçaoan culture post-slavery abolition."
            },
        {
            id: 2,
            title: "CURAÇAO ART",
            body: "A vibrant cultural hub showcasing contemporary and traditional Curaçaoan artistic expressions. Houses rotating exhibitions featuring prominent Caribbean artists, sculpture gardens, and interactive art installations that reflect the island's diverse cultural heritage."
        },
        {
            id: 3,
            title: "FORTCHURCH LIVING MONUMENT",
            body: "Historic fortified church structure serving as both a religious site and cultural landmark. Represents a unique blend of Dutch colonial architecture and Caribbean influences. Offers guided tours highlighting its role in Curaçao's religious and military history."
        },
        {
            id: 4,
            title: "LAMAN SPA AND WELLNESS",
            body: "Premium wellness destination offering traditional Caribbean healing treatments and modern spa therapies. Features ocean-view treatment rooms, natural local ingredients, and specialized relaxation programs inspired by island traditions."
        },
        {
            id: 5,
            title: "DISTRICT OTROBANDA",
            body: "Historic neighborhood known for its colorful Dutch colonial architecture and winding streets. Home to numerous cultural attractions, local markets, and authentic restaurants. Represents one of Curaçao's most well-preserved historic districts with stunning harbor views."
        }
    ])

    const handleNext = () => {
        if (hotelIndex < hotels.length - 4) {
            setHotelIndex(hotelIndex + 2);
        }
    };

    const handlePrev = () => {
        if (hotelIndex > 0) {
            setHotelIndex(hotelIndex - 2);
        }
    };

    const handleNextRes = () => {
        if (restaurantIndex < restaurants.length - 4) {
            setRestaurantIndex(restaurantIndex + 2);
        }
    };

    const handlePrevRes = () => {
        if (restaurantIndex > 0) {
            setRestaurantIndex(restaurantIndex - 1);
        }
    };

    const handleNextExp = () => {
        if (experienceIndex < experiencesCarousel.length - 4) {
            setExperienceIndex(experienceIndex + 2);
        }
    };

    const handlePrevExp = () => {
        if (experienceIndex > 0) {
            setExperienceIndex(experienceIndex - 2);
        }
    };

    // Beaches carousel handlers
    const handleNextBeach = () => { if (beachIndex < beaches.length - 4) setBeachIndex(beachIndex + 2); };
    const handlePrevBeach = () => { if (beachIndex > 0) setBeachIndex(beachIndex - 2); };

    // Explore carousel handlers
    const handleNextExplore = () => { if (exploreIndex < exploreItems.length - 4) setExploreIndex(exploreIndex + 2); };
    const handlePrevExplore = () => { if (exploreIndex > 0) setExploreIndex(exploreIndex - 2); };

    // Wellness carousel handlers
    const handleNextWellness = () => { if (wellnessIndex < wellnessItems.length - 4) setWellnessIndex(wellnessIndex + 2); };
    const handlePrevWellness = () => { if (wellnessIndex > 0) setWellnessIndex(wellnessIndex - 2); };

    // Play carousel handlers
    const handleNextPlay = () => { if (playIndex < playItems.length - 4) setPlayIndex(playIndex + 2); };
    const handlePrevPlay = () => { if (playIndex > 0) setPlayIndex(playIndex - 2); };

    // Shop Local carousel handlers
    const handleNextShopLocal = () => { if (shopLocalIndex < shopLocalItems.length - 4) setShopLocalIndex(shopLocalIndex + 2); };
    const handlePrevShopLocal = () => { if (shopLocalIndex > 0) setShopLocalIndex(shopLocalIndex - 2); };

    const renderCountryArticles = () => {
        if (!countryArticles || countryArticles.length === 0) return null;
        const [featured, ...rest] = countryArticles;
        const featuredLink = featured.slug ? `/article/${featured.slug}` : `/blog?id=${featured.id}`;
        return (
            <div className='country-articles-section'>
                <Link className='country-article-featured' to={featuredLink}>
                    <div className='country-article-featured-img' style={{backgroundImage: `url(${featured.image})`}}>
                        {featured.category && <span className='country-article-tag'>{featured.category}</span>}
                    </div>
                    <div className='country-article-featured-body'>
                        <h3 className='country-article-featured-title'>{featured.title}</h3>
                        <p className='country-article-featured-excerpt'>{featured.excerpt}</p>
                        <div className='country-article-meta'>
                            <span className='country-article-author'>By {featured.author}</span>
                            <span className='country-article-readmore'>Read Article →</span>
                        </div>
                    </div>
                </Link>
                {rest.length > 0 && (
                    <div className='country-articles-grid'>
                        {rest.map((a, i) => {
                            const link = a.slug ? `/article/${a.slug}` : `/blog?id=${a.id}`;
                            return (
                                <Link className='country-article-card' to={link} key={a.id || i}>
                                    <div className='country-article-card-img' style={{backgroundImage: `url(${a.image})`}}>
                                        {a.category && <span className='country-article-tag'>{a.category}</span>}
                                    </div>
                                    <div className='country-article-card-body'>
                                        <h4 className='country-article-card-title'>{a.title}</h4>
                                        <p className='country-article-card-excerpt'>{a.excerpt}</p>
                                        <span className='country-article-readmore'>Read Article →</span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    const renderHotels = () => {

        const render_hotels = hotels.map((hotel, index) => {
            return (
                <Link className='hotel-item' to={`/hotel/${slugify(hotel.title)}`} key={hotel.id || index}>
                    <div className='hotel-image' style={{backgroundImage: `url(${hotel.image})`, backgroundSize: 'cover'}}></div>
                    <div className='hotel-title'>{hotel.title}</div>
                    <div className='hotel-location'>
                        <FaMapPin size={12} color={'#2c2c2c'} /> 
                        <div className='hotel-address'>{hotel.address}</div>
                    </div>
                    <div className='hotel-descr'>{stripHtml(hotel.description)}</div>
                    <div className='hotel-btn'>{hotel.button.toUpperCase()}</div>
                </Link>
            )
        })

        return (
            <div className='hotels-track' style={{transform: `translateX(-${hotelIndex * 26}%)`}}>
                <div className='carousel-spacer'></div>
                {render_hotels}
            </div>
        )

    }

    const renderRestaurants = () => {

        const render_restaurants = restaurants.map((restaurant, index) => {
            return (
                <Link className='hotel-item' to={`/restaurant/${slugify(restaurant.title)}`} key={restaurant.id || index}>
                    <div className='hotel-image' style={{backgroundImage: `url(${restaurant.image})`, backgroundSize: 'cover'}}></div>
                    <div className='hotel-title'>{restaurant.title}</div>
                    <div className='hotel-location'>
                        <FaMapPin size={12} color={'#2c2c2c'} /> 
                        <div className='hotel-address'>{(restaurant.location || '').toUpperCase()}</div>
                    </div>
                    <div className='hotel-descr'>{stripHtml(restaurant.description)}</div>
                    <div className='hotel-btn'>{(restaurant.button || 'Reserve').toUpperCase()}</div>
                </Link>
            )
        })

        return (
            <div className='hotels-track' style={{transform: `translateX(-${restaurantIndex * 26}%)`}}>
                <div className='carousel-spacer'></div>
                {render_restaurants}
            </div>
        )

    }

    const renderExperiencesCarousel = () => {
        const items = experiencesCarousel.map((exp, index) => {
            return (
                <Link className='hotel-item' to={`/experience/${slugify(exp.title)}`} key={index}>
                    <div className='hotel-image' style={{backgroundImage: `url(${exp.image})`, backgroundSize: 'cover'}}></div>
                    <div className='hotel-title'>{exp.title}</div>
                    <div className='hotel-location'>
                        <FaMapPin size={12} color={'#2c2c2c'} /> 
                        <div className='hotel-address'>{exp.location.toUpperCase()}</div>
                    </div>
                    <div className='hotel-descr'>{stripHtml(exp.description)}</div>
                    <div className='hotel-btn'>{exp.button.toUpperCase()}</div>
                </Link>
            )
        })

        return (
            <div className='hotels-track' style={{transform: `translateX(-${experienceIndex * 26}%)`}}>
                <div className='carousel-spacer'></div>
                {items}
            </div>
        )
    }

    const renderBeachesCarousel = () => {
        const items = beaches.map((item, index) => (
            <Link className='hotel-item' to={`/beach/${slugify(item.title)}`} key={item.id || index}>
                <div className='hotel-image' style={{backgroundImage: `url(${item.image})`, backgroundSize: 'cover'}}></div>
                <div className='hotel-title'>{item.title}</div>
                <div className='hotel-location'>
                    <FaMapPin size={12} color={'#2c2c2c'} />
                    <div className='hotel-address'>{(item.location || '').toUpperCase()}</div>
                </div>
                <div className='hotel-descr'>{stripHtml(item.description)}</div>
                <div className='hotel-btn'>{(item.button || 'Explore').toUpperCase()}</div>
            </Link>
        ));
        return (<div className='hotels-track' style={{transform: `translateX(-${beachIndex * 26}%)`}}><div className='carousel-spacer'></div>{items}</div>);
    };

    const renderExploreCarousel = () => {
        const items = exploreItems.map((item, index) => (
            <Link className='hotel-item' to={`/explore/${slugify(item.title)}`} key={item.id || index}>
                <div className='hotel-image' style={{backgroundImage: `url(${item.image})`, backgroundSize: 'cover'}}></div>
                <div className='hotel-title'>{item.title}</div>
                <div className='hotel-location'>
                    <FaMapPin size={12} color={'#2c2c2c'} />
                    <div className='hotel-address'>{(item.location || '').toUpperCase()}</div>
                </div>
                <div className='hotel-descr'>{stripHtml(item.description)}</div>
                <div className='hotel-btn'>{(item.button || 'Discover').toUpperCase()}</div>
            </Link>
        ));
        return (<div className='hotels-track' style={{transform: `translateX(-${exploreIndex * 26}%)`}}><div className='carousel-spacer'></div>{items}</div>);
    };

    const renderWellnessCarousel = () => {
        const items = wellnessItems.map((item, index) => (
            <Link className='hotel-item' to={`/wellness/${slugify(item.title)}`} key={item.id || index}>
                <div className='hotel-image' style={{backgroundImage: `url(${item.image})`, backgroundSize: 'cover'}}></div>
                <div className='hotel-title'>{item.title}</div>
                <div className='hotel-location'>
                    <FaMapPin size={12} color={'#2c2c2c'} />
                    <div className='hotel-address'>{(item.location || '').toUpperCase()}</div>
                </div>
                <div className='hotel-descr'>{stripHtml(item.description)}</div>
                <div className='hotel-btn'>{(item.button || 'Book Now').toUpperCase()}</div>
            </Link>
        ));
        return (<div className='hotels-track' style={{transform: `translateX(-${wellnessIndex * 26}%)`}}><div className='carousel-spacer'></div>{items}</div>);
    };

    const renderPlayCarousel = () => {
        const items = playItems.map((item, index) => (
            <Link className='hotel-item' to={`/play/${slugify(item.title)}`} key={item.id || index}>
                <div className='hotel-image' style={{backgroundImage: `url(${item.image})`, backgroundSize: 'cover'}}></div>
                <div className='hotel-title'>{item.title}</div>
                <div className='hotel-location'>
                    <FaMapPin size={12} color={'#2c2c2c'} />
                    <div className='hotel-address'>{(item.location || '').toUpperCase()}</div>
                </div>
                <div className='hotel-descr'>{stripHtml(item.description)}</div>
                <div className='hotel-btn'>{(item.button || 'Learn More').toUpperCase()}</div>
            </Link>
        ));
        return (<div className='hotels-track' style={{transform: `translateX(-${playIndex * 26}%)`}}><div className='carousel-spacer'></div>{items}</div>);
    };

    const renderShopLocalCarousel = () => {
        const items = shopLocalItems.map((item, index) => (
            <Link className='hotel-item' to={`/shop/${slugify(item.title)}`} key={item.id || index}>
                <div className='hotel-image' style={{backgroundImage: `url(${item.image})`, backgroundSize: 'cover'}}></div>
                <div className='hotel-title'>{item.title}</div>
                <div className='hotel-location'>
                    <FaMapPin size={12} color={'#2c2c2c'} />
                    <div className='hotel-address'>{(item.location || '').toUpperCase()}</div>
                </div>
                <div className='hotel-descr'>{stripHtml(item.description)}</div>
                <div className='hotel-btn'>{(item.button || 'Visit').toUpperCase()}</div>
            </Link>
        ));
        return (<div className='hotels-track' style={{transform: `translateX(-${shopLocalIndex * 26}%)`}}><div className='carousel-spacer'></div>{items}</div>);
    };

    const toggleItem = (id) => {
        setActiveItem(id === activeItem ? null : id);
    };

    const renderExperiences = () => {

        const render_experiences = experiences.map((exp, index) => {
            return (
                <div
                key={exp.id}
                className={`attraction-item ${exp.id === activeItem ? 'active' : ''}`}
                onClick={() => toggleItem(exp.id)}
                >
                    <div className='attraction-header'>{exp.title}</div>
                    <div className="attraction-body">{exp.body}</div>
                </div>
            )
        })

        return (
            <div className='curaçao-attractions-menu'>
                {render_experiences}
            </div>
        );

    }

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className='country-loading'>
        <div className='loading-message'>Loading country information...</div>
      </div>
    );
  }
  
  // Show error message if API request failed
  if (error) {
    return (
      <div className='country-error'>
        <div className='error-message'>Error: {error}</div>
        <Link to='/' className='error-link'>Return to Home</Link>
      </div>
    );
  }
  
  return (
    <div className=''>
        <div className='landing-section' style={{backgroundImage: `url(${country.image})`, backgroundSize: 'cover'}}>
        </div>
        <div className='country-intro'>
            <h1 className='country-intro-title' onClick={() => {console.log(
                countryInfo
            )}}>{countryInfo.name} Travel Guide</h1>
            <div className='country-intro-body'>{country.intro.body}</div>
            <Link className='country-intro-read-more' to={`/country-profile/${countryId}`}>
                Read More
                <div style={{height: '100%', display: 'flex', alignItems: 'center'}}>
                    <MdChevronRight size={20} color='#2c2c2c'/>
                </div>
            </Link>
        </div>

        <div className='country-blogs-wrapper' style={{ display: 'flex', gap: '40px', margin: '0 auto', padding: '0 50px' }}>
            <div style={{ flex: 1 }}>
                <div className='country-section-header' style={{ padding: '0' }}>Plan your {countryInfo.name} Trip</div>
                <div style={{height: '4px', width: '80px', background: '#2c2c2c', marginBottom: '20px'}}></div>
                {travelGuide && (
                    <Link className='country-blog-link' to={`/travel-guide/${countryId}`} style={{ textDecoration: 'none', cursor: 'pointer', display: 'flex', gap: '20px' }}>
                        {travelGuide.image && (
                            <div className='blog-image' style={{
                                backgroundImage: `url(${travelGuide.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }} />
                        )}
                        <div style={{ flex: 1 }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '10px', color: '#2c2c2c' }}>
                                {travelGuide.title || `Plan your ${countryInfo.name} Trip`}
                            </h3>
                            {(travelGuide.excerpt) && (
                                <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#555', marginBottom: '15px' }}>
                                    {travelGuide.excerpt.substring(0, 150)}...
                                </p>
                            )}
                            <div style={{ 
                                display: 'inline-block',
                                padding: '10px 20px',
                                background: '#2c2c2c',
                                color: '#fff',
                                fontSize: '13px',
                                fontWeight: '600',
                                borderRadius: '4px'
                            }}>
                                Read More
                            </div>
                        </div>
                    </Link>
                )}
            </div>
            <div style={{ flex: 1 }}>
                <div className='country-section-header' style={{padding: '0'}}>The Queer Landscape</div>
                <div style={{height: '4px', width: '80px', background: '#2c2c2c', marginBottom: '20px'}}></div>
                {queerLandscape && (
                    <Link className='country-blog-link' to={`/queer-landscape/${countryId}`} style={{ textDecoration: 'none', cursor: 'pointer', display: 'flex', gap: '20px' }}>
                        {queerLandscape.image && (
                            <div className='blog-image' style={{
                                backgroundImage: `url(${queerLandscape.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }} />
                        )}
                        <div style={{ flex: 1 }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '10px', color: '#2c2c2c' }}>
                                {queerLandscape.title || `The Queer Landscape in ${countryInfo.name}`}
                            </h3>
                            {(queerLandscape.excerpt || queerLandscape.intro_text) && (
                                <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#555', marginBottom: '15px' }}>
                                    {(queerLandscape.excerpt || queerLandscape.intro_text).substring(0, 150)}...
                                </p>
                            )}
                            <div style={{ 
                                display: 'inline-block',
                                padding: '10px 20px',
                                background: '#2c2c2c',
                                color: '#fff',
                                fontSize: '13px',
                                fontWeight: '600',
                                borderRadius: '4px'
                            }}>
                                Read More
                            </div>
                        </div>
                    </Link>
                )}
            </div>
        </div>

        <div className='country-section-header'>Articles</div>
        <div className='container'>
            {renderCountryArticles()}
        </div>

        <div className='country-section-header'>Stay</div>
        <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button className="carousel-arrow left" onClick={handlePrev}>‹</button>

            <div className='country-hotels'>
                {renderHotels()}
            </div>
            
            <button className="carousel-arrow right" onClick={handleNext}>›</button>
        </div>
       
        
        <div className='country-section-header'>Eat & Drink</div>
        <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button className="carousel-arrow left" onClick={handlePrevRes}>‹</button>

            <div className='country-hotels'>
                {renderRestaurants()}
            </div>
            
            <button className="carousel-arrow right" onClick={handleNextRes}>›</button>
        </div>

        {/* Experiences section hidden
        <div className='country-section-header'>Experiences</div>
        <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button className="carousel-arrow left" onClick={handlePrevExp}>‹</button>

            <div className='country-hotels'>
                {renderExperiencesCarousel()}
            </div>
            
            <button className="carousel-arrow right" onClick={handleNextExp}>›</button>
        </div>
        */}

        {beaches.length > 0 && (<>
            <div className='country-section-header'>Beaches</div>
            <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
                <button className="carousel-arrow left" onClick={handlePrevBeach}>‹</button>
                <div className='country-hotels'>{renderBeachesCarousel()}</div>
                <button className="carousel-arrow right" onClick={handleNextBeach}>›</button>
            </div>
        </>)}

        {exploreItems.length > 0 && (<>
            <div className='country-section-header'>Explore</div>
            <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
                <button className="carousel-arrow left" onClick={handlePrevExplore}>‹</button>
                <div className='country-hotels'>{renderExploreCarousel()}</div>
                <button className="carousel-arrow right" onClick={handleNextExplore}>›</button>
            </div>
        </>)}

        {wellnessItems.length > 0 && (<>
            <div className='country-section-header'>Wellness & Ritual</div>
            <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
                <button className="carousel-arrow left" onClick={handlePrevWellness}>‹</button>
                <div className='country-hotels'>{renderWellnessCarousel()}</div>
                <button className="carousel-arrow right" onClick={handleNextWellness}>›</button>
            </div>
        </>)}

        {playItems.length > 0 && (<>
            <div className='country-section-header'>Play</div>
            <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
                <button className="carousel-arrow left" onClick={handlePrevPlay}>‹</button>
                <div className='country-hotels'>{renderPlayCarousel()}</div>
                <button className="carousel-arrow right" onClick={handleNextPlay}>›</button>
            </div>
        </>)}

        {shopLocalItems.length > 0 && (<>
            <div className='country-section-header'>Shop Local</div>
            <div className='carousel-wrapper' style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
                <button className="carousel-arrow left" onClick={handlePrevShopLocal}>‹</button>
                <div className='country-hotels'>{renderShopLocalCarousel()}</div>
                <button className="carousel-arrow right" onClick={handleNextShopLocal}>›</button>
            </div>
        </>)}
    </div>
  )
}
